import { JournalFeelingLinks } from "@/components/journal/JournalFeelingLinks";
import { friendlyError } from "@/lib/friendly-errors";
import {
  loadJournalDraft,
  saveJournalDraft,
  clearJournalDraft,
} from "@/lib/journal-autosave";
import { trackJournalCompleted, trackJournalStarted } from "@/lib/member-journey";
import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo, useEffect, useRef, lazy, Suspense } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  getJournalEntries,
  createJournalEntry,
  deleteJournalEntry,
  updateJournalEntry,
  regenerateReflection,
  toggleFavoriteJournalEntry,
  type AiResponse,
} from "@/lib/journal.functions";
import {
  JOURNAL_PROMPTS,
  getDailyPrompt,
  MOOD_OPTIONS,
  moodEmoji,
  PROMPT_CATEGORIES,
  WEEKLY_REVIEW_PROMPTS,
  SIMPLE_JOURNAL_STEPS,
  recommendationsForPrompt,
  type CategoryRecommendation,
} from "@/lib/journal-prompts";
import { AiConsentDialog, hasAiConsent } from "@/components/AiConsentDialog";
import { MobileJournalApp } from "@/components/journal/MobileJournalApp";
import { JournalEntrySummary } from "@/components/journal/JournalEntrySummary";
import { PromptReading } from "@/components/journal/PromptReading";
import { readingForPromptText } from "@/lib/journal-prompt-reading";
import { useIsNativeApp } from "@/lib/native-app";

/* Panels the member only opens on request — kept out of the first load so the
   writing surface appears sooner on a phone. */
const PromptLibrary = lazy(() =>
  import("@/components/journal/PromptLibrary").then((m) => ({
    default: m.PromptLibrary,
  })),
);
const GuidedJournalFlow = lazy(() =>
  import("@/components/journal/GuidedJournalFlow").then((m) => ({
    default: m.GuidedJournalFlow,
  })),
);
import { AudioReflectionRecorder } from "@/components/journal/AudioReflectionRecorder";
import { transcribeAudio } from "@/lib/transcribe.functions";

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
const VoiceRecorder = lazy(() =>
  import("@/components/VoiceRecorder").then((m) => ({
    default: m.VoiceRecorder,
  })),
);

function PanelSkeleton() {
  return (
    <div className="space-y-3" aria-hidden>
      <div className="h-10 animate-pulse rounded-2xl bg-muted" />
      <div className="h-24 animate-pulse rounded-2xl bg-muted" />
      <div className="h-24 animate-pulse rounded-2xl bg-muted" />
    </div>
  );
}
import { MoodStrip } from "@/components/journal/MoodStrip";
import { PromptCarousel } from "@/components/journal/PromptCarousel";
import { HelpMeBegin } from "@/components/journal/HelpMeBegin";
import { SavedCelebration } from "@/components/journal/SavedCelebration";
import { ReflectionsList } from "@/components/journal/ReflectionsList";
import { JournalDisclaimer } from "@/components/journal/JournalDisclaimer";
import { JournalInsights } from "@/components/journal/JournalInsights";
import { FirstReflectionProgress, FirstReflectionWelcome } from "@/components/journal/FirstReflectionWelcome";
import { ReflectionSupport } from "@/components/journal/ReflectionSupport";
import { DEPTH_STYLES, moodTint } from "@/lib/journal-mood-colors";
import { suggestedPrompts } from "@/lib/journal-suggestions";
import {
  promptByText,
  LIBRARY_PROMPTS,
  STARTER_PROMPT,
  DEPTH_LABELS,
  LIBRARY_PROMPT_COUNT,
  randomPrompt,
  tagLabel,
  type PromptDepth,
} from "@/lib/journal-prompt-library";
import { getRecentPrompts, pushRecentPrompt } from "@/lib/journal-prompt-prefs";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import {
  Sparkles,
  Users,

  Moon,
  ArrowRight,
  X,
  Trash2,
  Heart,
  Shuffle,
  Lightbulb,
  Compass,
  Star,
  Cloud,
  Leaf,
  Mic,
  PenLine,
  BookOpen,
  RefreshCw,
  ListChecks,
  Search,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { detectCrisis } from "@/lib/crisis-detection";
import { reportCrisisIfDetected } from "@/lib/crisis-report";
import { CrisisDialog } from "@/components/CrisisDialog";
import { notifyActivityChange } from "@/lib/activity-events";
import { matchesTopic } from "@/lib/topics";
import { suggestTappingForText } from "@/lib/tapping-data";
import { FavoriteButton } from "@/components/FavoriteButton";
import { readStoredAuthSession, resolveAuthSession } from "@/lib/auth-session";
import { JournalGuide } from "@/components/JournalGuide";
import {
  logSaveEvent,
  describeRequest,
  describeError,
} from "@/lib/journal-save-log";
import {
  enqueueOfflineDraft,
  flushOfflineDrafts,
  getOfflineDrafts,
  subscribeOfflineDrafts,
  type QueuedDraft,
} from "@/lib/journal-offline-queue";



export const Route = createFileRoute("/journal")({
  // Optional ?prompt= carries reflection context in from other sections
  // (e.g. the Integrate topics) so the writing surface opens on the right
  // question instead of the generic daily prompt.
  validateSearch: (search: Record<string, unknown>): { prompt?: string; pioneerDraft?: "1"; entry?: string } => ({
    ...(typeof search.entry === "string" && search.entry ? { entry: search.entry.slice(0, 64) } : {}),
    ...(typeof search.prompt === "string" && search.prompt.trim() ? { prompt: search.prompt.slice(0, 300) } : {}),
    ...(search.pioneerDraft === "1" ? { pioneerDraft: "1" as const } : {}),
  }),
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Recovery Journal | Trauma-Informed Prompts" },
      {
        name: "description",
        content:
          "A trauma-informed Recovery Journal with daily, self-compassion, and dissociation prompts. Gentle reflections that quietly suggest grounding, breathing, somatic, and inner-child tools when they may help.",
      },
      { property: "og:title", content: "Recovery Journal — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Daily, self-compassion, and dissociation prompts with contextual recovery tools — for reflection, self-awareness, and CPTSD recovery.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/journal" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/journal" },
    ],
  }),
  component: withMemberGate(JournalPage, "Recovery Journal"),
});

interface JournalEntry {
  id: string;
  title: string;
  content: string;
  mood: string | null;
  prompt: string | null;
  ai_response: AiResponse | null;
  supportive_reply: string | null;
  created_at: string;
  is_favorite?: boolean | null;
  source_mood_check_id?: string | null;
}

type GuestJournalEntry = JournalEntry;

function JournalPage() {
  const queryClient = useQueryClient();
  const fetchJournals = useServerFn(getJournalEntries);
  const createEntry = useServerFn(createJournalEntry);
  const removeEntry = useServerFn(deleteJournalEntry);
  const regenerate = useServerFn(regenerateReflection);
  const transcribe = useServerFn(transcribeAudio);
  const toggleFavorite = useServerFn(toggleFavoriteJournalEntry);
  const updateEntry = useServerFn(updateJournalEntry);
  const [journalTopic] = useState<string | null>(null);


  // Track auth so we can show a clear sign-in CTA instead of silent 401s.
  const [authReady, setAuthReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const isAuthed = !!session;
  useEffect(() => {
    let mounted = true;
    const storedSession = readStoredAuthSession();
    if (storedSession) {
      setSession(storedSession);
      setAuthReady(true);
    }
    resolveAuthSession(supabase).then((nextSession) => {
      if (!mounted) return;
      setSession(nextSession ?? null);
      setAuthReady(true);
    });
    const handler = (e: Event) => {
      const nextSession = (e as CustomEvent).detail?.session as Session | null | undefined;
      setSession(nextSession ?? null);
      setAuthReady(true);
    };
    window.addEventListener("rt-auth-change", handler);
    return () => {
      mounted = false;
      window.removeEventListener("rt-auth-change", handler);
    };
  }, []);

  const {
    data,
    isLoading,
    isError: entriesFailed,
    isFetching: entriesFetching,
    refetch: refetchEntries,
  } = useQuery({
    queryKey: ["journal-entries"],
    queryFn: fetchJournals,
    enabled: isAuthed,
    // Reflections rarely change between visits — serve them instantly on
    // return, refresh quietly in the background.
    staleTime: 60_000,
    // A dropped mobile connection should retry a couple of times before the
    // member ever sees an error; a real 401/403 should not be retried.
    retry: (failureCount, error) => {
      const message = error instanceof Error ? error.message : "";
      if (/\b(401|403|404)\b|unauthor/i.test(message)) return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 6000),
  });

  const [guestEntries, setGuestEntries] = useState<GuestJournalEntry[]>([]);
  const [noticeAccepted, setNoticeAccepted] = useState<boolean | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("rt:guest-journal-entries");
      if (raw) setGuestEntries(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    try {
      setNoticeAccepted(
        localStorage.getItem("rt:journal-gentle-notice-v1") === "yes",
      );
    } catch {
      setNoticeAccepted(false);
    }
  }, []);

  const acceptGentleNotice = () => {
    try {
      localStorage.setItem("rt:journal-gentle-notice-v1", "yes");
    } catch {
      /* ignore */
    }
    setNoticeAccepted(true);
  };

  const serverEntries = (data?.entries ?? []) as unknown as JournalEntry[];
  const entries = isAuthed ? serverEntries : guestEntries;
  // Journal Insights receives metadata only. Written content, titles and AI
  // responses never cross this boundary.
  const insightEntries = useMemo(
    () => entries.map(({ id, mood, prompt, created_at }) => ({ id, mood, prompt, created_at })),
    [entries],
  );
  const dailyPrompt = useMemo(() => getDailyPrompt(), []);
  const dailyCompassion = useMemo(() => {
    const reminders = [
      "💛 You don't need to have all the answers today.",
      "💛 Your feelings are valid — all of them.",
      "💛 Healing happens one reflection at a time.",
      "💛 You are allowed to take your time.",
      "💛 Being here, even briefly, is enough.",
      "💛 You can be tender with yourself today.",
      "💛 There is nothing to fix in this moment.",
    ];
    const day = Math.floor(Date.now() / 86_400_000);
    return reminders[day % reminders.length];
  }, []);
  const [activePrompt, setActivePrompt] = useState<string>(dailyPrompt);
  const [isWriting, setIsWriting] = useState(true);
  // The entry that was just saved, so we can confirm gently inline instead of
  // opening a full-screen panel over the member straight after they save.
  const [lastSaved, setLastSaved] = useState<JournalEntry | null>(null);
  const writingSurfaceRef = useRef<HTMLElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [content, setContent] = useState("");
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [openEntry, setOpenEntry] = useState<JournalEntry | null>(null);
  const [reflectionStatus, setReflectionStatus] = useState<
    "idle" | "generating" | "retrying" | "saved" | "error"
  >("idle");
  const [reflectionAttempt, setReflectionAttempt] = useState(0);
  const [reflectionError, setReflectionError] = useState<string | null>(null);
  // The AI note is shown once, before the very first reflection is created.
  const [firstAiNoteFor, setFirstAiNoteFor] = useState<string | null>(null);
  const MAX_REFLECTION_ATTEMPTS = 3;
  const [showSafety, setShowSafety] = useState(false);
  const [safetyDismissedFor, setSafetyDismissedFor] = useState<string>("");
  const [showVoice, setShowVoice] = useState(false);
  const [promptQuery, setPromptQuery] = useState("");
  const [promptFocused, setPromptFocused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showGuided, setShowGuided] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<{
    type: "info" | "success" | "error" | "auth";
    message: string;
  } | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  // Dream-Journal-style collapsible flow: Feelings → Write → Prompts.
  const [openJournalSection, setOpenJournalSection] = useState<"feelings" | "write" | "prompts">("prompts");
  const [journalMode, setJournalMode] = useState<"record" | "write" | null>("write");
  const [firstReflectionCompleted, setFirstReflectionCompleted] = useState(false);
  const [beginFirstReflectionSignal, setBeginFirstReflectionSignal] = useState(0);

  useEffect(() => {
    if (!session?.user.id) return;
    try {
      setWelcomeDismissed(
        localStorage.getItem(`rt:journal-welcome-dismissed:${session.user.id}`) === "yes",
      );
    } catch {
      setWelcomeDismissed(false);
    }
  }, [session?.user.id]);

  const categoryList = useMemo(() => {
    return [
      { id: "all", title: "All" },
      ...PROMPT_CATEGORIES.map((c) => ({ id: c.id, title: c.title })),
    ];
  }, []);

  const allPrompts = useMemo(() => {
    const seen = new Set<string>();
    const items: { prompt: string; category: string }[] = [];
    for (const cat of PROMPT_CATEGORIES) {
      for (const p of cat.prompts) {
        if (seen.has(p)) continue;
        seen.add(p);
        items.push({ prompt: p, category: cat.title });
      }
    }
    for (const p of JOURNAL_PROMPTS) {
      if (seen.has(p)) continue;
      seen.add(p);
      items.push({ prompt: p, category: "Daily Reflection" });
    }
    return items;
  }, []);

  const filteredPrompts = useMemo(() => {
    let results = allPrompts;
    if (selectedCategory && selectedCategory !== "all") {
      const catTitle = PROMPT_CATEGORIES.find((c) => c.id === selectedCategory)?.title;
      results = results.filter((p) => p.category === catTitle);
    }
    const q = promptQuery.trim().toLowerCase();
    if (q) {
      results = results.filter(
        (p) =>
          p.prompt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q),
      );
    }
    return results.slice(0, 12);
  }, [promptQuery, selectedCategory, allPrompts]);

  // Set once the regenerate mutation below exists, so saving can kick off the
  // reflection automatically without re-ordering the hooks.
  const startReflectionRef = useRef<((vars: { id: string }) => void) | null>(null);

  const createMutation = useMutation({
    mutationFn: (vars: {
      title: string;
      content: string;
      mood?: string;
      prompt?: string;
    }) => saveJournalEntry(vars),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      trackJournalCompleted("journal");
      notifyActivityChange("journal");
      setSaveNotice({ type: "success", message: "Reflection saved. Held with care." });
      clearJournalDraft(session?.user?.id ?? "guest");
      setContent("");
      setSelectedMood(null);
      setIsWriting(false);
      // Stay where the member is. Nothing opens over the top of them — they
      // choose whether to open the entry, with a calm inline confirmation.
      const e = res.entry as unknown as JournalEntry;
      setLastSaved(e?.id ? e : null);
      if (e?.id) setOpenEntry(e);
      setFirstReflectionCompleted(true);
      toast.success("Your reflection is saved.");
      // The gentle reflection arrives on its own — but the first time, the AI
      // note is shown first so nothing is sent to the AI service unannounced.
      if (e?.id) {
        if (hasAiConsent()) startReflectionRef.current?.({ id: e.id });
        else setFirstAiNoteFor(e.id);
      }
    },
    onError: (err: Error, vars) => {
      logSaveEvent({
        level: "error",
        phase: "mutation-error",
        request: describeRequest(vars),
        error: describeError(err),
      });
      const message = err.message || "Could not save your reflection.";
      const offline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      const isNetwork = /load failed|network|failed to fetch|timeout|timed out|aborted|econn/i.test(
        err.message || "",
      );

      // Never lose the user's writing: persist to guest local storage as a
      // safety net so the reflection can be recovered / re-tried later.
      try {
        const GUEST_KEY = "rt:guest-journal-entries";
        const raw = typeof window !== "undefined" ? localStorage.getItem(GUEST_KEY) : null;
        const existing: GuestJournalEntry[] = raw ? JSON.parse(raw) : [];
        const entry: GuestJournalEntry = {
          id: `guest-${Date.now()}`,
          title: vars.title,
          content: vars.content,
          mood: vars.mood ?? null,
          prompt: vars.prompt ?? null,
          ai_response: null,
          supportive_reply: null,
          created_at: new Date().toISOString(),
        };
        const next = [entry, ...existing].slice(0, 200);
        localStorage.setItem(GUEST_KEY, JSON.stringify(next));
        setGuestEntries(next);
      } catch {
        /* ignore */
      }
      const isTimeoutErr = /timeout|timed out|taking too long|aborted/i.test(
        err.message || "",
      );

      // If the failure looks like a connectivity issue, queue the draft in
      // the offline outbox so it syncs automatically when we're back online.
      if (isAuthed && (offline || isNetwork)) {
        enqueueOfflineDraft({
          title: vars.title,
          content: vars.content,
          mood: vars.mood,
          prompt: vars.prompt,
        });
        setSaveNotice({
          type: "info",
          message: isTimeoutErr
            ? "The connection is slow — your reflection is safely queued and will sync as soon as it comes through. Feel free to keep writing."
            : offline
              ? "You're offline. Draft saved on this device — it'll sync automatically when your connection returns."
              : "Connection dropped. Draft saved on this device — it'll sync automatically as soon as we can reach the server.",
        });
        toast.success(
          isTimeoutErr
            ? "Slow connection — we'll keep trying in the background."
            : "Draft saved offline. We'll sync it when you're back online.",
        );
        // On timeout keep the user on their entry with their text intact so
        // they never see an error page. Only clear the composer if the
        // browser is fully offline (they know they can't keep saving).
        if (!isTimeoutErr && offline) {
          // The words are safely queued in the offline outbox, so the
          // in-progress draft is no longer needed.
          clearJournalDraft(session?.user?.id ?? "guest");
          setContent("");
          setSelectedMood(null);
          setIsWriting(false);
        }
        return;
      }


      setSaveNotice({
        type: "error",
        message: `${message} We kept a copy on this device so it's safe.`,
      });
      toast.error(message);
    },
  });

  const saveJournalEntry = async (vars: {
    title: string;
    content: string;
    mood?: string;
    prompt?: string;
  }) => {
    const isTimeout = (err: unknown) => {
      const msg = friendlyError(err);
      return /timeout|timed out|taking too long|aborted/i.test(msg);
    };
    const isTransient = (err: unknown) => {
      const msg = friendlyError(err);
      return /load failed|network|failed to fetch|timeout|timed out|taking too long|aborted|econn/i.test(msg);
    };

    // Timeouts get a dedicated escalation ladder so a slow connection has
    // three chances to land the save before we surface anything to the user.
    const TIMEOUT_LADDER_MS = [30_000, 45_000, 60_000];
    const BACKOFF_MS = [800, 1_600, 3_200];
    const MAX_ATTEMPTS = TIMEOUT_LADDER_MS.length;

    const attempt = async (attemptNo: number) => {
      const timeoutMs = TIMEOUT_LADDER_MS[attemptNo] ?? 60_000;
      const startedAt = Date.now();
      const startedIso = new Date(startedAt).toISOString();
      const timeout = new Promise<never>((_, reject) => {
        window.setTimeout(
          () => reject(
            new Error(
              "Saving is taking too long. Please check your connection and try again.",
            ),
          ),
          timeoutMs,
        );
      });
      const save = async () => {
        // Ensure we have a fresh session token before calling the server fn.
        // On mobile (especially Safari/Chrome backgrounded tabs) the cached
        // session may have expired; getSession() refreshes it automatically.
        const sess = await resolveAuthSession(supabase, 1_200);
        if (!sess?.user.id) {
          throw new Error("Please sign in again to save your reflection.");
        }
        const res = await createEntry({ data: vars });
        return { entry: res.entry as unknown as JournalEntry };
      };
      try {
        const out = await Promise.race([save(), timeout]);
        logSaveEvent({
          level: "info",
          phase: "success",
          attempt: attemptNo,
          startedAt: startedIso,
          durationMs: Date.now() - startedAt,
          request: describeRequest(vars),
        });
        return out;
      } catch (err) {
        logSaveEvent({
          level: "error",
          phase: "attempt-failed",
          attempt: attemptNo,
          startedAt: startedIso,
          durationMs: Date.now() - startedAt,
          timeoutMs,
          transient: isTransient(err),
          request: describeRequest(vars),
          error: describeError(err),
        });
        throw err;
      }
    };

    let lastErr: unknown;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        return await attempt(i);
      } catch (err) {
        lastErr = err;
        const timeout = isTimeout(err);
        const transient = isTransient(err);
        // Non-transient (e.g. auth) fails fast. Transient errors get retried;
        // timeouts get the full ladder, other transients stop after 2.
        if (!transient) break;
        if (!timeout && i >= 1) break;
        if (i === MAX_ATTEMPTS - 1) break;
        // Nudge the user without alarming them so a slow save still feels alive.
        setSaveNotice({
          type: "info",
          message: timeout
            ? `Still saving — the connection is slow. Retrying (attempt ${i + 2} of ${MAX_ATTEMPTS})…`
            : "Retrying your save…",
        });
        await new Promise((r) => window.setTimeout(r, BACKOFF_MS[i] ?? 3_200));
      }
    }
    logSaveEvent({
      level: "error",
      phase: "gave-up",
      request: describeRequest(vars),
      error: describeError(lastErr),
    });
    throw lastErr instanceof Error
      ? lastErr
      : new Error("Could not save your reflection.");
  };


  // ---- Offline outbox sync ------------------------------------------------
  const [offlineQueue, setOfflineQueue] = useState<QueuedDraft[]>(
    () => (typeof window !== "undefined" ? getOfflineDrafts() : []),
  );

  useEffect(() => subscribeOfflineDrafts(setOfflineQueue), []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isAuthed) return;

    let cancelled = false;
    const runFlush = async () => {
      if (!navigator.onLine) return;
      if (getOfflineDrafts().length === 0) return;
      const { synced } = await flushOfflineDrafts((vars) =>
        saveJournalEntry(vars),
      );
      if (cancelled) return;
      if (synced > 0) {
        queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
        notifyActivityChange("journal");
        toast.success(
          synced === 1
            ? "1 offline draft synced to your journal."
            : `${synced} offline drafts synced to your journal.`,
        );
      }
    };

    // Try on mount, on regained connectivity, and when tab returns to view.
    void runFlush();
    const onOnline = () => void runFlush();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void runFlush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // saveJournalEntry is stable within this render; re-run when auth flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);






  const deleteMutation = useMutation({
    mutationFn: async (vars: { id: string; transcript?: string }) => {
      if (vars.id.startsWith("guest-")) {
        const next = guestEntries.filter((e) => e.id !== vars.id);
        setGuestEntries(next);
        if (typeof window !== "undefined") {
          localStorage.setItem("rt:guest-journal-entries", JSON.stringify(next));
        }
        return { ok: true } as const;
      }
      return removeEntry({ data: vars });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      setOpenEntry(null);
      toast.success("Entry removed.");
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: async (vars: { id: string; is_favorite: boolean }) => {
      if (vars.id.startsWith("guest-")) {
        const next = guestEntries.map((e) =>
          e.id === vars.id ? { ...e, is_favorite: vars.is_favorite } : e,
        );
        setGuestEntries(next);
        if (typeof window !== "undefined") {
          localStorage.setItem("rt:guest-journal-entries", JSON.stringify(next));
        }
        return { success: true, is_favorite: vars.is_favorite } as const;
      }
      return toggleFavorite({ data: vars });
    },
    onMutate: (vars) => {
      // Optimistic open-entry update so the heart fills instantly in the detail view.
      if (openEntry?.id === vars.id) {
        setOpenEntry({ ...openEntry, is_favorite: vars.is_favorite });
      }
    },
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      toast.success(vars.is_favorite ? "Added to favourites." : "Removed from favourites.");
    },
    onError: (err: Error, vars) => {
      if (openEntry?.id === vars.id) {
        setOpenEntry({ ...openEntry, is_favorite: !vars.is_favorite });
      }
      toast.error(err.message || "Couldn't update favourite.");
    },
  });

  // Editing today's (or any) reflection — same entry, never a duplicate.
  const updateMutation = useMutation({
    mutationFn: async (vars: { id: string; content: string; mood: string | null }) => {
      if (vars.id.startsWith("guest-")) {
        const next = guestEntries.map((e) =>
          e.id === vars.id ? { ...e, content: vars.content, mood: vars.mood } : e,
        );
        setGuestEntries(next);
        if (typeof window !== "undefined") {
          localStorage.setItem("rt:guest-journal-entries", JSON.stringify(next));
        }
        return { success: true } as const;
      }
      const existing = entries.find((e) => e.id === vars.id);
      return updateEntry({
        data: {
          id: vars.id,
          title: (existing?.title || "Reflection").slice(0, 200),
          content: vars.content,
          mood: vars.mood,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      setUpdatingId(null);
      toast.success("Your reflection has been updated.");
    },
    onError: (err: Error) => {
      setUpdatingId(null);
      toast.error(err.message || "Couldn't save your changes.");
    },
  });


  const regenerateMutation = useMutation({
    mutationFn: async (vars: { id: string; transcript?: string }) => {
      // Use the typed server function (auth attached automatically via the
      // start.ts middleware). Retry once on transient network failures — iOS
      // Safari commonly aborts the first request with "Load failed" when the
      // connection stalls or the tab briefly backgrounds.
      const isTransient = (err: unknown) => {
        const msg = friendlyError(err);
        return /load failed|network|failed to fetch|timeout|timed out|aborted|econn/i.test(msg);
      };

      const attempt = async (attemptNo: number): Promise<{ entry: JournalEntry }> => {
        const timeoutMs = attemptNo === 0 ? 30_000 : 45_000;
        const timeout = new Promise<never>((_, reject) => {
          window.setTimeout(
            () => reject(new Error("timeout: reflection took too long")),
            timeoutMs,
          );
        });
        return Promise.race([
          regenerate({ data: { id: vars.id, transcript: vars.transcript } }) as Promise<{ entry: JournalEntry }>,
          timeout,
        ]);
      };

      let lastErr: unknown;
      for (let i = 0; i < MAX_REFLECTION_ATTEMPTS; i++) {
        setReflectionAttempt(i + 1);
        setReflectionStatus(i === 0 ? "generating" : "retrying");
        // Persistent toast that updates across attempts, so users get
        // feedback even if they've scrolled away from the reflection card.
        if (i === 0) {
          toast.loading("Creating your reflection…", {
            id: "reflection-toast",
            description: "This can take up to 30 seconds.",
          });
        } else {
          toast.loading(`Retrying (attempt ${i + 1} of ${MAX_REFLECTION_ATTEMPTS})…`, {
            id: "reflection-toast",
            description: "Your connection looked slow — trying again.",
          });
        }
        try {
          return await attempt(i);
        } catch (err) {
          lastErr = err;
          if (!isTransient(err) || i === MAX_REFLECTION_ATTEMPTS - 1) break;
          // Backoff: 800ms, then 1600ms
          await new Promise((r) => window.setTimeout(r, 800 * (i + 1)));
        }
      }
      const msg = friendlyError(lastErr, "Could not generate a reflection.");
      throw new Error(
        /timeout/i.test(msg)
          ? "Reflection is taking too long on your connection. Please try again in a moment."
          : msg,
      );
    },
    onMutate: (vars) => {
      setReflectionStatus("generating");
      setReflectionAttempt(1);
      setReflectionError(null);
      const pending = (prev: JournalEntry | null) =>
        prev && prev.id === vars.id
          ? { ...prev, ai_response: null, supportive_reply: "Creating reflection…" }
          : prev;
      setOpenEntry(pending);
      setLastSaved(pending);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
      const e = res.entry as unknown as JournalEntry;
      if (e) {
        setOpenEntry((prev) => (prev && prev.id === e.id ? e : prev));
        setLastSaved((prev) => (prev && prev.id === e.id ? e : prev));
      }
      setReflectionStatus("saved");
      setReflectionAttempt(0);
      setReflectionError(null);
      window.setTimeout(() => {
        setReflectionStatus((s) => (s === "saved" ? "idle" : s));
      }, 3000);
      toast.success("A new reflection has arrived.", {
        id: "reflection-toast",
        description: "It's open on your screen now.",
        duration: 6000,
      });
    },
    onError: (err: Error, vars) => {
      setReflectionStatus("error");
      setReflectionError(err.message || "Could not generate a reflection.");
      const fallback: AiResponse = {
        reflection:
          "Thank you for taking a moment to write. Whatever you shared is welcome here — there is no wrong way to feel today. A fresh reflection couldn't reach you just now, but your words have been saved safely.",
        insight:
          "Showing up for this reflection is itself an act of self-care. Your willingness to notice is part of recovery.",
        practice: {
          title: "3-minute grounding",
          description:
            "A short, gentle exercise to settle the nervous system through breath and the senses.",
          href: "/grounding",
        },
        followUp: "What feels even slightly supportive or comforting right now?",
      };
      const withFallback = (prev: JournalEntry | null) =>
        prev && prev.id === vars.id
          ? { ...prev, ai_response: fallback, supportive_reply: fallback.reflection }
          : prev;
      setOpenEntry(withFallback);
      setLastSaved(withFallback);
      toast.error("Couldn't reach the reflection service", {
        id: "reflection-toast",
        description:
          err.message ||
          "We tried a few times. A gentle fallback is showing — tap Try again when you're ready.",
        duration: 10000,
        action: {
          label: "Try again",
          onClick: () => regenerateMutation.mutate({ id: vars.id }),
        },
      });
    },
  });

  startReflectionRef.current = regenerateMutation.mutate;




  const scrollWritingIntoView = () => {
    if (typeof window === "undefined") return;
    const go = () =>
      writingSurfaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    requestAnimationFrame(() => {
      go();
      // The prompt library collapses on selection, so the page height changes
      // right after the first scroll — re-aim once the layout has settled.
      window.setTimeout(go, 120);
      // Focus the textarea after the scroll starts so mobile keyboards
      // don't fight the smooth-scroll animation.
      window.setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 420);
    });
  };

  // Apply an incoming ?prompt= once, on first mount, without clobbering the
  // member's own prompt choice afterwards.
  // Journey tracking: one "started" event per visit to the writing view.
  const journalStartTracked = useRef(false);
  useEffect(() => {
    if (!isWriting || journalStartTracked.current) return;
    journalStartTracked.current = true;
    trackJournalStarted("journal");
  }, [isWriting]);

  const { prompt: incomingPrompt, pioneerDraft, entry: returnEntryId } = Route.useSearch();
  // Coming back from the Emotion Wheel or a practice: reopen that reflection.
  const returnEntryApplied = useRef(false);
  useEffect(() => {
    if (!returnEntryId || returnEntryApplied.current || !entries?.length) return;
    const match = (entries as JournalEntry[]).find((e) => e.id === returnEntryId);
    if (!match) return;
    returnEntryApplied.current = true;
    setOpenEntry(match);
  }, [returnEntryId, entries]);
  const incomingPromptApplied = useRef(false);
  useEffect(() => {
    if (!incomingPrompt || incomingPromptApplied.current) return;
    incomingPromptApplied.current = true;
    setActivePrompt(incomingPrompt);
    setIsWriting(true);
  }, [incomingPrompt]);

  useEffect(() => {
    if (pioneerDraft !== "1" || incomingPromptApplied.current) return;
    incomingPromptApplied.current = true;
    const raw = window.sessionStorage.getItem("rt:pioneer-journal-draft");
    window.sessionStorage.removeItem("rt:pioneer-journal-draft");
    if (!raw) return;
    try {
      const draft = JSON.parse(raw) as { prompt?: unknown; content?: unknown };
      if (typeof draft.prompt !== "string" || !draft.prompt.trim()) return;
      setActivePrompt(draft.prompt.slice(0, 300));
      if (typeof draft.content === "string") setContent(draft.content);
      setIsWriting(true);
    } catch {
      // A malformed or expired handoff leaves the ordinary journal untouched.
    }
  }, [pioneerDraft]);

  const startWithPrompt = (prompt: string) => {
    setActivePrompt(prompt);
    createMutation.reset();
    setSaveNotice(null);
    setLastSaved(null);
    setIsWriting(true);
    // A library choice must open the actual journal editor. Previously the
    // prompt changed in state while the writing section could remain closed,
    // so tapping a result appeared to do nothing.
    setJournalMode("write");
    setOpenJournalSection("write");
    setShowLibrary(false);
    const match = promptByText(prompt);
    if (match) pushRecentPrompt(match.id);
    scrollWritingIntoView();
  };

  const startGuidedResult = ({
    prompt,
    content: prefill,
  }: {
    prompt: string;
    content?: string;
  }) => {
    if (prefill) setContent(prefill);
    setShowGuided(false);
    startWithPrompt(prompt);
  };



  // AI disclosure: personal journal text only leaves the app after the member
  // has agreed once to the inline AI consent (App Review Guideline 5.1.2).
  const [aiConsentOpen, setAiConsentOpen] = useState(false);
  const pendingAiAction = useRef<(() => void) | null>(null);

  const withAiConsent = (run: () => void) => {
    if (hasAiConsent()) {
      run();
      return;
    }
    pendingAiAction.current = run;
    setAiConsentOpen(true);
  };

  const saveReflection = () => {
    if (!content.trim()) {
      setSaveNotice({ type: "error", message: "Write a few words before saving." });
      return;
    }
    if (createMutation.isPending) return;
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (!authReady) {
      // Session is still resolving. Show visible feedback so the tap is never
      // silently ignored on mobile, and retry once the auth state arrives.
      setSaveNotice({ type: "info", message: "Just a moment — preparing your journal…" });
      window.setTimeout(() => saveReflection(), 400);
      return;
    }
    if (!isAuthed) {
      // Guests (any device) can save unlimited reflections locally for testing/feedback.
      const GUEST_KEY = "rt:guest-journal-entries";
      try {
        const raw =
          typeof window !== "undefined" ? localStorage.getItem(GUEST_KEY) : null;
        const existing: GuestJournalEntry[] = raw ? JSON.parse(raw) : [];
        const entry: GuestJournalEntry = {
          id: `guest-${Date.now()}`,
          title: activePrompt.slice(0, 120),
          content: content.trim(),
          mood: selectedMood,
          prompt: activePrompt,
          ai_response: null,
          supportive_reply: null,
          created_at: new Date().toISOString(),
        };
        const next = [entry, ...existing].slice(0, 200);
        localStorage.setItem(GUEST_KEY, JSON.stringify(next));
        setGuestEntries(next);
        setSaveNotice({
          type: "success",
          message:
            "Saved to this browser ✨ Sign in any time to keep your reflections safely in your private journal.",
        });
        toast.success("Reflection saved on this device.");
        setIsWriting(false);
        clearJournalDraft(draftOwnerId);
        setContent("");
        setSelectedMood(null);
      } catch {
        setSaveNotice({
          type: "auth",
          message:
            "We couldn't save locally on this browser. Try signing in to save your reflection privately to your journal.",
        });
      }
      return;
    }
    if (detectCrisis(content) && safetyDismissedFor !== content) {
      setShowSafety(true);
      // continue saving — we do not block the entry
    }
    void reportCrisisIfDetected({ text: content, source: "journal" });
    setSaveNotice({ type: "info", message: "Saving your reflection…" });
    // Build a minimal payload: send only the fields that carry new info.
    // Skipping empty/default fields shaves bytes off the request, which
    // meaningfully speeds saves on 3G / spotty mobile connections.
    const trimmedContent = content.replace(/[ \t]+\n/g, "\n").trim();
    const title = activePrompt.slice(0, 120).trim();
    const payload: {
      title: string;
      content: string;
      mood?: string;
      prompt?: string;
    } = { title, content: trimmedContent };
    if (selectedMood) payload.mood = selectedMood;
    // Omit prompt when it's just today's default — the server already knows it.
    if (activePrompt && activePrompt !== dailyPrompt) {
      payload.prompt = activePrompt;
    }
    createMutation.mutate(payload);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveReflection();
  };

  const handleContentChange = (value: string) => {
    setContent(value);
    if (saveNotice) setSaveNotice(null);
    if (lastSaved) setLastSaved(null);
    if (detectCrisis(value) && !showSafety && safetyDismissedFor !== value) {
      setShowSafety(true);
    }
  };

  // ---- Autosave the reflection being written ------------------------------
  // Everything typed is kept on this device a moment after the member stops
  // typing, so a dropped connection, a closed tab or a phone restart never
  // loses their words. The draft is removed as soon as the reflection is saved.
  const draftOwnerId = session?.user?.id ?? "guest";
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const draftRestoreDone = useRef(false);

  useEffect(() => {
    if (!authReady || draftRestoreDone.current) return;
    draftRestoreDone.current = true;
    // A prompt or pioneer hand-off arriving in the URL wins — the member came
    // here to start something specific.
    if (incomingPrompt || pioneerDraft === "1") return;
    const draft = loadJournalDraft(draftOwnerId);
    if (!draft) return;
    setContent((current) => (current.trim() ? current : draft.content));
    if (draft.mood) setSelectedMood((m) => m ?? draft.mood);
    if (draft.prompt) setActivePrompt(draft.prompt);
    setIsWriting(true);
    setDraftSavedAt(draft.savedAt || Date.now());
    setDraftRestored(true);
  }, [authReady, draftOwnerId, incomingPrompt, pioneerDraft]);

  useEffect(() => {
    if (!draftRestoreDone.current) return;
    const handle = window.setTimeout(() => {
      if (!content.trim()) {
        clearJournalDraft(draftOwnerId);
        setDraftSavedAt(null);
        return;
      }
      const at = saveJournalDraft(draftOwnerId, {
        content,
        mood: selectedMood,
        prompt: activePrompt ?? null,
      });
      if (at) setDraftSavedAt(at);
    }, 800);
    return () => window.clearTimeout(handle);
  }, [content, selectedMood, activePrompt, draftOwnerId]);

  // Write immediately when the tab is hidden or closed — the riskiest moments.
  useEffect(() => {
    const flush = () => {
      if (!content.trim()) return;
      saveJournalDraft(draftOwnerId, {
        content,
        mood: selectedMood,
        prompt: activePrompt ?? null,
      });
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
    };
  }, [content, selectedMood, activePrompt, draftOwnerId]);

  const forgetDraft = () => {
    clearJournalDraft(draftOwnerId);
    setContent("");
    setDraftSavedAt(null);
    setDraftRestored(false);
  };

  const draftStatusNode =
    draftRestored && content.trim() ? (
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-gold/30 bg-gold/10 px-3 py-2 text-xs text-foreground">
        <span>
          We kept what you were writing on this device. You can carry on where you left off.
        </span>
        <button
          type="button"
          onClick={forgetDraft}
          className="shrink-0 rounded-full border border-border/60 px-3 py-1 font-medium hover:bg-background/70"
        >
          Start fresh
        </button>
      </div>
    ) : draftSavedAt && content.trim() ? (
      <p className="mt-2 text-xs text-muted-foreground">
        Saved on this device — your writing is kept even if the connection drops.
      </p>
    ) : null;



  // Emotional landscape — last 30 days
  const landscape = useMemo(() => {
    const cutoff = Date.now() - 30 * 86_400_000;
    const counts = new Map<string, number>();
    for (const e of entries) {
      if (!e.mood) continue;
      if (new Date(e.created_at).getTime() < cutoff) continue;
      counts.set(e.mood, (counts.get(e.mood) ?? 0) + 1);
    }
    const total = [...counts.values()].reduce((a, b) => a + b, 0);
    return { counts, total };
  }, [entries]);

  const streak = useMemo(() => {
    if (entries.length === 0) return 0;
    const days = new Set(
      entries.map((e) =>
        new Date(e.created_at).toISOString().slice(0, 10),
      ),
    );
    let count = 0;
    const cursor = new Date();
    for (;;) {
      const key = cursor.toISOString().slice(0, 10);
      if (days.has(key)) {
        count++;
        cursor.setDate(cursor.getDate() - 1);
      } else if (count === 0) {
        // allow today to be missed
        cursor.setDate(cursor.getDate() - 1);
        if (!days.has(cursor.toISOString().slice(0, 10))) break;
      } else {
        break;
      }
    }
    return count;
  }, [entries]);

  const weekSummary = useMemo(() => {
    const cutoff = Date.now() - 7 * 86_400_000;
    const recent = entries.filter(
      (e) => new Date(e.created_at).getTime() >= cutoff,
    );
    const moodCounts = new Map<string, number>();
    for (const e of recent) {
      if (e.mood) moodCounts.set(e.mood, (moodCounts.get(e.mood) ?? 0) + 1);
    }
    const top = [...moodCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    return { count: recent.length, top };
  }, [entries]);

  // Suggestions come only from what the member deliberately selected — never
  // from reading or analysing their private writing.
  const [recentTags, setRecentTags] = useState<string[]>([]);
  useEffect(() => {
    const ids = getRecentPrompts();
    const tags = ids
      .map((id) => LIBRARY_PROMPTS.find((p) => p.id === id))
      .flatMap((p) => p?.tags ?? []);
    setRecentTags(tags.slice(0, 6));
  }, [entries.length]);

  const carouselPrompts = useMemo(
    () => suggestedPrompts({ mood: selectedMood, recentTags }),
    [selectedMood, recentTags],
  );

  const lastPromptDepth: PromptDepth = promptByText(activePrompt)?.depth ?? "gentle";
  const celebrate = saveNotice?.type === "success";

  // Phone-app layout: swipeable Feel / Write / Prompts cards plus a deck of
  // journal cards. Members can switch to the full journal at any time.
  const nativeApp = useIsNativeApp();
  const [appView, setAppView] = useState(true);
  // The swipe view is the default inside the phone app. On a phone browser it
  // can be opened with /journal?view=app and is then remembered on that device.
  const [preferAppView, setPreferAppView] = useState(false);
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("view") === "app") {
        localStorage.setItem("rt:journal-app-view", "1");
        setPreferAppView(true);
      } else if (q.get("view") === "full") {
        localStorage.removeItem("rt:journal-app-view");
      } else if (localStorage.getItem("rt:journal-app-view") === "1") {
        setPreferAppView(true);
      }
    } catch {
      /* private browsing — the full journal view is used */
    }
  }, []);
  const appViewAvailable = nativeApp || preferAppView;
  // Everyone now uses the same guided Record/Write flow.
  const useAppLayout = false && appViewAvailable && appView;
  const hasWrittenJournalReflection = entries.some((entry) => !entry.source_mood_check_id);
  const isFirstReflection =
    isAuthed && !isLoading && !hasWrittenJournalReflection && !firstReflectionCompleted;
  const showFirstReflectionWelcome = isFirstReflection && !welcomeDismissed;

  const dismissFirstReflectionWelcome = () => {
    if (session?.user.id) {
      try {
        localStorage.setItem(`rt:journal-welcome-dismissed:${session.user.id}`, "yes");
      } catch {
        /* private browsing — dismiss for this visit */
      }
    }
    setWelcomeDismissed(true);
  };

  const beginFirstReflection = () => {
    setIsWriting(true);
    if (useAppLayout) {
      setBeginFirstReflectionSignal((value) => value + 1);
      window.requestAnimationFrame(() => {
        document.getElementById("journal-writing-flow")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }
    scrollWritingIntoView();
  };

  return (
    <div className="relative min-h-dvh overflow-hidden bg-background">
      {noticeAccepted === false && (
        <GentleNoticeGate onContinue={acceptGentleNotice} />
      )}
      {/* Ambient healing background — soft lavender sky */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#e9defb]/70 via-[#f3e7f7]/40 to-transparent" />
        <div className="absolute -top-32 start-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-[#d9c8f5]/40 blur-3xl" />
        <div className="absolute top-40 end-[-4rem] h-72 w-72 rounded-full bg-[#f1d6ec]/50 blur-3xl" />
        <div className="absolute top-[28rem] start-[-3rem] h-64 w-64 rounded-full bg-[#e3d4f7]/40 blur-3xl" />
        <div className="absolute bottom-0 end-1/3 h-80 w-80 rounded-full bg-[#efe3fb]/50 blur-3xl" />

        {/* Floating stars */}
        <Star className="absolute top-16 start-[8%] h-3 w-3 animate-pulse text-amber-300/70" style={{ animationDuration: "4s" }} />
        <Star className="absolute top-40 end-[18%] h-2 w-2 animate-pulse text-amber-200/70" style={{ animationDuration: "5s" }} />
        <Star className="absolute top-[18rem] start-[28%] h-2.5 w-2.5 animate-pulse text-amber-300/60" style={{ animationDuration: "6s" }} />
        <Star className="absolute top-[34rem] end-[8%] h-2 w-2 animate-pulse text-amber-200/60" style={{ animationDuration: "5.5s" }} />
        <Star className="absolute bottom-40 start-[12%] h-2 w-2 animate-pulse text-amber-300/60" style={{ animationDuration: "4.5s" }} />

        {/* Moon accents */}
        <Moon className="absolute top-24 end-[10%] h-5 w-5 text-[#c9b6ec]/60" />
        <Moon className="absolute bottom-32 end-[28%] h-4 w-4 text-[#c9b6ec]/50" />

        {/* Soft clouds */}
        <Cloud className="absolute top-[12rem] start-[5%] h-10 w-10 text-white/60" />
        <Cloud className="absolute top-[40rem] end-[12%] h-12 w-12 text-white/50" />
        <Cloud className="absolute bottom-20 start-[30%] h-8 w-8 text-white/60" />

        {/* Gentle organic leaves */}
        <Leaf className="absolute top-[22rem] end-[5%] h-5 w-5 -rotate-12 text-[#b8d4b8]/50" />
        <Leaf className="absolute bottom-48 start-[6%] h-4 w-4 rotate-45 text-[#b8d4b8]/50" />
      </div>


      <div className="mx-auto max-w-4xl px-4 py-8 pb-[calc(env(safe-area-inset-bottom)+6rem)] sm:py-12">
        {/* Header — small, so writing starts within seconds */}
        <header className="mb-5">
          <h1 className="inline-flex items-center gap-2 font-serif text-3xl text-foreground sm:text-4xl">
            <Moon className="h-6 w-6 text-[var(--gold-deep)]" aria-hidden />
            Recovery Journal
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            A private space to put down what you're carrying.
          </p>
          {appViewAvailable && !appView && (
            <button
              type="button"
              onClick={() => setAppView(true)}
              className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 text-xs font-semibold text-foreground"
            >
              Back to the swipe view
            </button>
          )}
          <JournalDisclaimer className="mt-3" />
        </header>

        {showFirstReflectionWelcome && (
          <FirstReflectionWelcome
            onBegin={beginFirstReflection}
            onDismiss={dismissFirstReflectionWelcome}
          />
        )}

        {celebrate ? (
          <div className="mb-8">
            <SavedCelebration
              needsGrounding={lastPromptDepth !== "gentle"}
              onSeeReflection={() => {
                if (!lastSaved) return;
                setOpenEntry(lastSaved);
                setSaveNotice(null);
                document.getElementById("newly-saved-reflection")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              onFinish={() => {
                setSaveNotice(null);
                setLastSaved(null);
              }}
            />
          </div>
        ) : useAppLayout ? (
          <div id="journal-writing-flow" className="scroll-mt-24">
          <MobileJournalApp
            selectedMood={selectedMood}
            onMoodChange={setSelectedMood}
            content={content}
            onContentChange={handleContentChange}
            draftStatus={draftStatusNode}
            activePrompt={activePrompt && activePrompt !== dailyPrompt ? activePrompt : null}
            promptDepth={lastPromptDepth}
            onClearPrompt={() => setActivePrompt(dailyPrompt)}
            prompts={carouselPrompts}
            onSelectPrompt={startWithPrompt}
            onBrowseAll={() => {
              setShowLibrary(true);
              setAppView(false);
              window.requestAnimationFrame(() => {
                document.getElementById("journal-prompt-library")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              });
            }}
            promptCount={LIBRARY_PROMPT_COUNT}
            onHelpMeBegin={() => setShowHelp(true)}
            onRecord={() => setShowVoice(true)}
            voiceRecorderOpen={showVoice}
            onSave={saveReflection}
            saving={createMutation.isPending}
            saveError={saveNotice?.type === "error" ? saveNotice.message : null}
            entries={entries.filter((e) => matchesTopic(e, journalTopic))}
            insightEntries={insightEntries}
            loadingEntries={isLoading && isAuthed}
            onOpenEntry={(e) => setOpenEntry(e as JournalEntry)}
            onToggleFavorite={(e) =>
              favoriteMutation.mutate({ id: e.id, is_favorite: !e.is_favorite })
            }
            onSwitchToFullView={() => setAppView(false)}
            firstReflection={isFirstReflection}
            beginFirstReflectionSignal={beginFirstReflectionSignal}
          />
          </div>
        ) : (
          <div className="space-y-4">
            {/* FEEL */}
            <section className="rounded-3xl border border-[var(--qa-rose-ink)]/20 bg-gradient-to-br from-[var(--qa-rose-tint)] via-card to-[var(--qa-peach-tint)] p-4 shadow-sm sm:p-5">
              <h2 className="font-serif text-lg text-foreground">How are you arriving today?</h2>
              <p className="mb-3 mt-0.5 text-xs text-muted-foreground">Choose a feeling, if it helps.</p>
              <MoodStrip
                value={selectedMood}
                onChange={setSelectedMood}
                disabled={createMutation.isPending}
              />
            </section>

            {/* START */}

            {journalMode === "record" && (
              <section className="rounded-3xl border border-[var(--qa-rose-ink)]/20 bg-card p-4 shadow-sm sm:p-5" aria-label="Record your reflection">
                <h2 className="font-serif text-lg text-foreground">Record Your Reflection</h2>
                <p className="mb-4 mt-0.5 text-xs text-muted-foreground">You can listen back before saving.</p>
                <AudioReflectionRecorder
                  userId={session?.user.id ?? null}
                  mood={selectedMood}
                  prompt={activePrompt && activePrompt !== dailyPrompt ? activePrompt : null}
                  onSaved={async (saved) => {
                    queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
                    setJournalMode("write");
                    toast.success("Your recording is saved.");
                    const e = saved.entry as unknown as JournalEntry;
                    setLastSaved(e);
                    setOpenEntry(e);
                    if (!hasAiConsent()) {
                      setFirstAiNoteFor(e.id);
                      return;
                    }
                    let transcript: string | undefined;
                    try {
                      const base64 = await blobToBase64(saved.blob);
                      const res = (await transcribe({ data: { audioBase64: base64, mimeType: saved.blob.type || "audio/webm" } })) as { text?: string };
                      transcript = res?.text?.trim() || undefined;
                    } catch {
                      transcript = undefined;
                    }
                    regenerateMutation.mutate({ id: e.id, transcript });
                  }}
                />
                <button
                  type="button"
                  onClick={() => setJournalMode("write")}
                  className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-[var(--gold-deep)] hover:underline"
                >
                  <PenLine className="h-3.5 w-3.5" aria-hidden />
                  Prefer writing? Switch to writing instead
                </button>
              </section>
            )}

            {/* WRITE */}
            {journalMode === "write" && (
            <JournalSection
              id="write"
              icon={<PenLine className="h-5 w-5" />}
              title="Write Your Reflection"
              summary={content.trim() ? `${content.trim().split(/\s+/).length} words written` : "Take your time. This is your space."}
              open
              onToggle={() => setJournalMode("write")}
            >
            <div ref={writingSurfaceRef as React.RefObject<HTMLDivElement>} className="scroll-mt-24">
              {isFirstReflection && (
                <FirstReflectionProgress mood={selectedMood} hasWriting={Boolean(content.trim())} />
              )}

              {activePrompt && activePrompt !== dailyPrompt && (
                <div
                  className={`mt-3 flex items-start justify-between gap-3 rounded-2xl border p-3 ${
                    DEPTH_STYLES[lastPromptDepth].card
                  }`}
                >
                  <p className="font-serif text-sm leading-snug text-foreground">
                    <span aria-hidden>{DEPTH_STYLES[lastPromptDepth].emoji}</span>{" "}
                    {activePrompt}
                  </p>
                  <button
                    type="button"
                    onClick={() => setActivePrompt(dailyPrompt)}
                    aria-label="Clear this prompt"
                    className="shrink-0 rounded-full p-2 text-muted-foreground hover:bg-background/70"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-4">
                {authReady && !isAuthed && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-foreground">
                    <span>
                      You're browsing as a guest. Sign in to save your reflection and see it
                      in your journal history.
                    </span>
                    <Link
                      to="/login" search={{ redirect: "/journal" }}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-foreground px-4 py-1.5 text-xs font-semibold text-background hover:opacity-90"
                    >
                      Sign in
                    </Link>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  placeholder="Start writing here — there is no wrong way to begin…"
                  rows={6}
                  disabled={createMutation.isPending}
                  aria-busy={createMutation.isPending}
                  aria-label="What's on your mind?"
                  className="w-full resize-none rounded-2xl border border-gold/25 bg-background/70 p-4 font-serif text-base leading-relaxed text-foreground placeholder:text-foreground/60 min-h-[200px] [field-sizing:content] focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <TappingSuggestion text={content} />
                {draftStatusNode}

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setJournalMode("record")}
                    disabled={createMutation.isPending}
                    className="inline-flex min-h-[46px] flex-1 items-center justify-center gap-2 rounded-full border border-sky-500/40 bg-sky-500/10 px-4 text-sm font-medium text-foreground disabled:opacity-50 sm:flex-none"
                  >
                    <Mic className="h-4 w-4 text-sky-600 dark:text-sky-300" aria-hidden />
                    🎙️ Record instead
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHelp(true)}
                    disabled={createMutation.isPending}
                    className="inline-flex min-h-[46px] flex-1 items-center justify-center gap-2 rounded-full border border-gold/50 bg-gradient-to-r from-gold/25 to-blush/20 px-4 text-sm font-semibold text-foreground disabled:opacity-50 sm:flex-none"
                  >
                    <Sparkles className="h-4 w-4 text-[var(--gold-deep)]" aria-hidden />
                    ✨ Help me begin
                  </button>
                </div>

                <div className={`${showVoice ? "hidden " : ""}mt-5 flex flex-col gap-2`}>
                  {offlineQueue.length > 0 && (
                    <div
                      role="status"
                      aria-live="polite"
                      className="flex w-full items-center gap-2 rounded-xl border border-gold/40 bg-gold/10 px-3 py-2 text-sm text-foreground"
                    >
                      <Loader2
                        className="h-4 w-4 shrink-0 animate-spin text-[var(--gold-deep)]"
                        aria-hidden="true"
                      />
                      <span className="flex-1">
                        {offlineQueue.length === 1
                          ? "1 draft waiting to sync"
                          : `${offlineQueue.length} drafts waiting to sync`}
                        {typeof navigator !== "undefined" && !navigator.onLine
                          ? " — you're offline right now."
                          : " — syncing…"}
                      </span>
                    </div>
                  )}
                  {(saveNotice || createMutation.isPending) && (
                    <div
                      role="status"
                      aria-live="polite"
                      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm ${
                        saveNotice?.type === "error"
                          ? "border border-destructive/40 bg-destructive/10 text-destructive"
                          : saveNotice?.type === "auth"
                            ? "border border-gold/50 bg-gold/10 text-foreground"
                            : "border border-gold/40 bg-gold/10 text-foreground"
                      }`}
                    >
                      {(createMutation.isPending || saveNotice?.type === "info") && (
                        <>
                          {createMutation.isPending ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                          ) : (
                            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                          )}
                          <span>{saveNotice?.message || "Saving your reflection…"}</span>
                        </>
                      )}
                      {saveNotice?.type === "auth" && !createMutation.isPending && (
                        <>
                          <AlertCircle
                            className="h-4 w-4 shrink-0 text-[var(--gold-deep)]"
                            aria-hidden="true"
                          />
                          <span className="flex-1">{saveNotice.message}</span>
                          <Link
                            to="/login"
                            search={{ redirect: "/journal" }}
                            className="shrink-0 rounded-full border border-gold/50 bg-gold/20 px-3 py-1 text-xs font-semibold text-foreground hover:bg-gold/30"
                          >
                            Sign in
                          </Link>
                        </>
                      )}
                      {saveNotice?.type === "error" && !createMutation.isPending && (
                        <>
                          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span className="flex-1">{saveNotice.message}</span>
                          <button
                            type="button"
                            onClick={saveReflection}
                            className="shrink-0 rounded-full border border-destructive/40 px-3 py-1 text-xs font-semibold hover:bg-destructive/10"
                          >
                            Try again
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={saveReflection}
                    disabled={!content.trim() || createMutation.isPending}
                    aria-busy={createMutation.isPending}
                    className="relative z-20 inline-flex min-h-[50px] w-full touch-manipulation items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        <span>Saving…</span>
                      </>
                    ) : (
                      <>
                        Save Reflection
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
            </JournalSection>
            )}

            {/* A few prompts, if wanted — never more than three, always swipeable */}
            <JournalSection
              id="prompts"
              icon={<Sparkles className="h-5 w-5" />}
              title="Prompt ideas (optional)"
              summary={activePrompt && activePrompt !== dailyPrompt ? "A prompt is guiding your writing" : `${LIBRARY_PROMPT_COUNT}+ gentle prompts to choose from`}
              open={openJournalSection === "prompts"}
              onToggle={() => setOpenJournalSection(openJournalSection === "prompts" ? "write" : "prompts")}
            >
              <div className="mb-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLibrary(true);
                    window.requestAnimationFrame(() => {
                      document.getElementById("journal-prompt-library")?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    });
                  }}
                  className="shrink-0 text-xs font-semibold text-foreground underline underline-offset-4"
                >
                  Browse all {LIBRARY_PROMPT_COUNT}+ prompts →
                </button>
              </div>
              <PromptCarousel prompts={carouselPrompts} onSelect={(t) => { setJournalMode("write"); startWithPrompt(t); }} />
            </JournalSection>

            {showLibrary && (
              <div id="journal-prompt-library" className="mb-8 scroll-mt-24">
                <Suspense fallback={<PanelSkeleton />}>
                  <PromptLibrary onSelect={startWithPrompt} />
                </Suspense>
              </div>
            )}

            {showGuided && (
              <div id="journal-guided-flow" className="mb-8 scroll-mt-24">
                <Suspense fallback={<PanelSkeleton />}>
                  <GuidedJournalFlow
                    onWrite={startGuidedResult}
                    onClose={() => setShowGuided(false)}
                  />
                </Suspense>
              </div>
            )}
          </div>
        )}

        {/* SAVE → history */}
        <div
          id="newly-saved-reflection"
          className={`${useAppLayout && !celebrate ? "hidden" : "mb-8"} scroll-mt-24`}
        >
          {entriesFailed && isAuthed && (
            <div className="mb-4 rounded-2xl border border-border bg-background/70 p-4 text-sm text-foreground/80">
              <p>
                Your saved reflections didn&apos;t load just now. Nothing has
                been lost — this is usually a connection hiccup.
              </p>
              <button
                type="button"
                onClick={() => void refetchEntries()}
                disabled={entriesFetching}
                className="mt-3 inline-flex min-h-[48px] items-center justify-center rounded-full border border-gold/30 px-5 text-sm font-medium text-foreground/80 transition hover:bg-gold-light/30 disabled:opacity-60"
              >
                {entriesFetching ? "Trying again…" : "Try again"}
              </button>
            </div>
          )}

          <ReflectionsList
            entries={entries.filter((e) => matchesTopic(e, journalTopic))}
            loading={isLoading && isAuthed}
            updatingId={updateMutation.isPending ? updatingId : null}
            onOpen={(e) => setOpenEntry(e as JournalEntry)}
            onDelete={(id) => deleteMutation.mutate({ id })}
            onToggleFavorite={(e) =>
              favoriteMutation.mutate({ id: e.id, is_favorite: !e.is_favorite })
            }
            onUpdate={(vars) => {
              setUpdatingId(vars.id);
              updateMutation.mutate(vars);
            }}
          />
        </div>

        {/* Optional, always your choice: bring a few words to the community */}
        <div className={`${useAppLayout && !celebrate ? "hidden" : ""} mb-8 rounded-2xl border border-border/60 bg-muted/30 p-4 text-center`}>
          <p className="text-sm text-muted-foreground">
            Your journal stays private. If you'd like, you can share how you're feeling with
            the community — you choose the words before anything is posted.
          </p>
          <Link
            to="/community"
            onClick={() => {
              try {
                const seed = content.trim();
                if (seed) sessionStorage.setItem("rt_community_share_draft", seed);
              } catch {
                /* private browsing — the composer simply opens empty */
              }
            }}
            className="mt-3 inline-flex min-h-[44px] touch-manipulation items-center justify-center gap-2 rounded-full border border-border bg-card px-5 text-sm font-medium text-foreground shadow-sm"
          >
            <Users className="h-4 w-4" aria-hidden="true" />
            Share how you feel in Community
          </Link>
        </div>

        {/* Emotional landscape (collapsed) */}
        {!useAppLayout && (
          <div className="mb-4">
            <JournalInsights entries={insightEntries} />
          </div>
        )}

        {landscape.total > 0 && (
          <details className="group mb-4 rounded-3xl border border-blush/40 bg-card/60">
            <summary className="flex min-h-[52px] cursor-pointer items-center justify-between gap-2 rounded-3xl px-5 text-sm font-medium text-foreground hover:bg-card">
              <span className="inline-flex items-center gap-2">
                <Heart className="h-4 w-4 text-rose-500" aria-hidden />
                🌱 Your emotional landscape — last 30 days
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-5 pb-6">
              <p className="mb-4 text-sm text-muted-foreground">
                The feelings present in the last 30 days. None are wrong; all are welcome.
              </p>
              <div className="flex flex-wrap gap-3">
                {MOOD_OPTIONS.filter((m) => landscape.counts.get(m.value)).map((m) => {
                  const count = landscape.counts.get(m.value)!;
                  const ratio = count / landscape.total;
                  const size = 56 + Math.round(ratio * 70);
                  return (
                    <div
                      key={m.value}
                      className={`flex flex-col items-center justify-center rounded-full border text-center shadow-sm ${
                        moodTint(m.value).soft
                      }`}
                      style={{ width: `${size}px`, height: `${size}px` }}
                      title={`${m.value} · ${count}`}
                    >
                      <span className="text-2xl leading-none" aria-hidden>
                        {m.emoji}
                      </span>
                      <span className="mt-1 text-[10px] font-medium text-foreground/80">
                        {m.value} · {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        )}

        {/* New to journaling (collapsed) */}
        <details className="group mb-4 rounded-3xl border border-sage/40 bg-card/60">
          <summary className="flex min-h-[52px] cursor-pointer items-center justify-between gap-2 rounded-3xl px-5 text-sm font-medium text-foreground hover:bg-card">
            <span className="inline-flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-emerald-600 dark:text-emerald-300" aria-hidden />
              📖 New to journaling? — tap to learn the basics
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-5 pb-6">
            <p className="text-sm leading-relaxed text-foreground/85">
              Journaling is simply a way to explore your thoughts, emotions, experiences and
              needs. A few honest sentences are enough — the goal is awareness, not perfection.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-4">
                <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  <ListChecks className="h-3.5 w-3.5" />
                  Simple journaling process
                </div>
                <ol className="space-y-1.5 text-sm text-foreground/85">
                  {SIMPLE_JOURNAL_STEPS.map((step, i) => (
                    <li key={step} className="flex gap-2">
                      <span className="font-serif text-[var(--gold-deep)]">{i + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="rounded-2xl border border-purple-500/25 bg-purple-500/10 p-4">
                <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-purple-700 dark:text-purple-300">
                  <Compass className="h-3.5 w-3.5" />
                  Weekly reflection
                </div>
                <ul className="space-y-1.5 text-sm text-foreground/85">
                  {WEEKLY_REVIEW_PROMPTS.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span aria-hidden>•</span>
                      <button
                        type="button"
                        onClick={() => startWithPrompt(p)}
                        className="text-start underline-offset-2 hover:underline"
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </details>

        {/* Your week in reflection (collapsed) */}
        {weekSummary.count > 0 && (
          <details className="group mb-4 rounded-3xl border border-lavender/30 bg-card/60">
            <summary className="flex min-h-[52px] cursor-pointer items-center justify-between gap-2 rounded-3xl px-5 text-sm font-medium text-foreground hover:bg-card">
              <span className="inline-flex items-center gap-2">
                <Moon className="h-4 w-4 text-lavender-deep" aria-hidden />
                📅 Your week in reflection — tap to view
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-5 pb-6">
              <p className="font-serif text-xl text-foreground">
                {weekSummary.count}{" "}
                {weekSummary.count === 1 ? "reflection" : "reflections"} this week.
              </p>
              {weekSummary.top.length > 0 && (
                <p className="mt-3 text-sm text-foreground/80">
                  Most present:{" "}
                  {weekSummary.top.map(([mood, n], i) => (
                    <span key={mood}>
                      <span className="font-medium">
                        {moodEmoji(mood)} {mood}
                      </span>{" "}
                      <span className="text-muted-foreground">({n})</span>
                      {i < weekSummary.top.length - 1 ? " · " : ""}
                    </span>
                  ))}
                </p>
              )}
              <p className="mt-3 text-sm text-muted-foreground">
                Returning to yourself, even briefly, is the practice. Keep going gently.
              </p>
            </div>
          </details>
        )}

        <HelpMeBegin
          open={showHelp}
          onClose={() => setShowHelp(false)}
          onSurprise={() => {
            setShowHelp(false);
            startWithPrompt(randomPrompt().text);
          }}
          onGuide={() => {
            setShowHelp(false);
            // The guided questions and the prompt library live in the full
            // journal view, so leave the compact app view first — otherwise
            // the choice appears to do nothing.
            setAppView(false);
            setShowGuided(true);
            window.setTimeout(() => {
              document.getElementById("journal-guided-flow")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 250);
          }}
          onBrowse={() => {
            setShowHelp(false);
            setAppView(false);
            setShowLibrary(true);
            window.setTimeout(() => {
              document.getElementById("journal-prompt-library")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 250);
          }}
        />
      </div>

      {openEntry && (
        <EntryDetail
          entry={openEntry}
          onClose={() => setOpenEntry(null)}
          onDelete={() => {
            if (confirm("Release this reflection?")) {
              deleteMutation.mutate({ id: openEntry.id });
            }
          }}
          onRegenerate={() =>
            withAiConsent(() => regenerateMutation.mutate({ id: openEntry.id }))
          }
          onToggleFavorite={() =>
            favoriteMutation.mutate({
              id: openEntry.id,
              is_favorite: !openEntry.is_favorite,
            })
          }
          regenerating={regenerateMutation.isPending}
          status={reflectionStatus}
          attempt={reflectionAttempt}
          maxAttempts={MAX_REFLECTION_ATTEMPTS}
          errorMessage={reflectionError}
        />
      )}

      {/* Shown once, before the first automatic reflection is created. */}
      <AiConsentDialog
        open={firstAiNoteFor !== null}
        onAgree={() => {
          const id = firstAiNoteFor;
          setFirstAiNoteFor(null);
          if (id) startReflectionRef.current?.({ id });
        }}
        onDecline={() => setFirstAiNoteFor(null)}
      />

      <AiConsentDialog
        open={aiConsentOpen}
        onAgree={() => {
          setAiConsentOpen(false);
          const run = pendingAiAction.current;
          pendingAiAction.current = null;
          run?.();
        }}
        onDecline={() => {
          pendingAiAction.current = null;
          setAiConsentOpen(false);
        }}
      />



      {showSafety && (
        <CrisisDialog
          footerNote="Your reflection is saved. We're still here with you."
          onClose={() => {
            setSafetyDismissedFor(content);
            setShowSafety(false);
          }}
        />
      )}

      {showVoice && (
        <Suspense fallback={null}>
          <VoiceRecorder
            open={showVoice}
            onClose={() => setShowVoice(false)}
            onTranscript={(text) => {
              setIsWriting(true);
              setContent((prev) => (prev.trim() ? prev.trim() + "\n\n" + text : text));
            }}
            title="Record your reflection"
            description="Speak whatever wants to come through. You can review and edit before saving."
            accent="gold"
          />
        </Suspense>
      )}
      <div className="mt-8">
        <ComplianceNotice variant="journal" />
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border border-border/40 bg-card/70 p-4 backdrop-blur">
      <div className="mb-1 inline-flex max-w-full items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        <span className="shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className="break-words font-serif text-lg text-foreground">{value}</div>
    </div>
  );
}


function EntryCard({
  entry,
  onOpen,
  onToggleFavorite,
  onDelete,
}: {
  entry: JournalEntry;
  onOpen: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}) {
  const date = new Date(entry.created_at);
  const preview = entry.content.slice(0, 110);
  const reflectionPreview =
    entry.ai_response?.reflection ?? entry.supportive_reply ?? "";
  const isFav = !!entry.is_favorite;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative flex cursor-pointer flex-col rounded-3xl border border-border/40 bg-card/80 p-5 text-start backdrop-blur transition-all hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-md"
    >
      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Moon className="h-3 w-3 text-gold" />
          {date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
        <div className="flex items-center gap-2">
          {entry.mood && (
            <span className="rounded-full bg-blush/50 px-2 py-0.5 text-[11px] font-medium text-foreground">
              {moodEmoji(entry.mood)} {entry.mood}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite();
            }}
            aria-pressed={isFav}
            aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
            title={isFav ? "Remove from favourites" : "Add to favourites"}
            className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors ${
              isFav
                ? "border-rose-300 bg-rose-50 text-rose-500"
                : "border-border/50 bg-card text-muted-foreground hover:border-rose-300 hover:text-rose-500"
            }`}
          >
            <Heart className={`h-3.5 w-3.5 ${isFav ? "fill-current" : ""}`} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Delete this reflection"
            title="Delete this reflection"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border/50 bg-card text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {entry.prompt && (
        <p className="mb-2 line-clamp-2 font-serif text-base text-foreground">
          {entry.prompt}
        </p>
      )}

      <p className="line-clamp-2 text-sm text-foreground/80">
        {preview}
        {entry.content.length > 110 ? "…" : ""}
      </p>

      {reflectionPreview && (
        <div className="mt-3 rounded-xl border border-lavender/30 bg-lavender/10 p-3">
          <div className="mb-1 inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-lavender-deep">
            <Sparkles className="h-3 w-3" />
            Reflection
          </div>
          <p className="line-clamp-2 text-xs leading-relaxed text-foreground/85">
            {reflectionPreview}
          </p>
        </div>
      )}

      <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-foreground/70 group-hover:text-foreground">
        View full reflection
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </div>
    </div>
  );
}

function EntryDetail({
  entry,
  onClose,
  onDelete,
  onRegenerate,
  onToggleFavorite,
  regenerating,
  status,
  attempt,
  maxAttempts,
  errorMessage,
}: {
  entry: JournalEntry;
  onClose: () => void;
  onDelete: () => void;
  onRegenerate: () => void;
  onToggleFavorite: () => void;
  regenerating: boolean;
  status: "idle" | "generating" | "retrying" | "saved" | "error";
  attempt: number;
  maxAttempts: number;
  errorMessage: string | null;
}) {
  const ai = entry.ai_response;
  const isFav = !!entry.is_favorite;
  const detailScrollRef = useRef<HTMLDivElement>(null);
  const aiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    detailScrollRef.current?.scrollTo({ top: 0 });
  }, [entry.id]);

  // When a reflection is being prepared or arrives, bring it into view so
  // members never have to hunt for it.
  const hasAi = !!ai;
  useEffect(() => {
    if (!regenerating && !hasAi) return;
    const t = window.setTimeout(
      () => aiRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      150,
    );
    return () => window.clearTimeout(t);
  }, [regenerating, hasAi, entry.id]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Saved reflection"
      className="fixed inset-0 z-[160] flex bg-background"
    >
      <div
        className="relative mx-auto flex h-full w-full max-w-2xl flex-col overflow-hidden bg-card pt-[env(safe-area-inset-top)] sm:border-x sm:border-gold/30"
      >

        <div className="flex shrink-0 items-center justify-between border-b border-border/40 bg-card/95 px-6 py-4 backdrop-blur">

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Moon className="h-3.5 w-3.5 text-gold" />
            {new Date(entry.created_at).toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onToggleFavorite}
              aria-pressed={isFav}
              aria-label={isFav ? "Remove from favourites" : "Add to favourites"}
              title={isFav ? "Remove from favourites" : "Add to favourites"}
              className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition-colors ${
                isFav
                  ? "border-rose-300 bg-rose-50 text-rose-500"
                  : "border-border/50 bg-card text-muted-foreground hover:border-rose-300 hover:text-rose-500"
              }`}
            >
              <Heart className={`h-4 w-4 ${isFav ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border/50 bg-card text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>


        <div
          ref={detailScrollRef}
          className="flex-1 space-y-6 overflow-y-auto overscroll-contain bg-gradient-to-b from-[oklch(0.985_0.012_95)] via-card to-card px-6 py-6 sm:px-8 sm:py-8"
        >
          <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/12 via-card to-[oklch(0.97_0.025_330)] p-5 text-center">
            <h2 className="font-serif text-2xl text-foreground">Your Reflection 💛</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground/80">
              This is a moment you chose to put into words. You don&apos;t have to solve
              everything you wrote here. You can simply notice what was present for you.
            </p>
          </div>

          <JournalDisclaimer />

          {entry.prompt && (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-gold">
                Prompt
              </div>
              <p className="font-serif text-xl text-foreground">
                {entry.prompt}
              </p>
            </div>
          )}

          <div>
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Your reflection
              {entry.mood && (
                <span className="ms-2 rounded-full bg-blush/50 px-2 py-0.5 text-[10px] normal-case tracking-normal text-foreground">
                  {moodEmoji(entry.mood)} {entry.mood}
                </span>
              )}
            </div>
            <p className="whitespace-pre-wrap font-serif text-base leading-relaxed text-foreground">
              {entry.content}
            </p>
          </div>

          <div ref={aiRef} id="entry-ai-reflection" className="scroll-mt-4">
          {regenerating && !ai ? (
            <div role="status" aria-live="polite" className="flex items-center gap-2 rounded-2xl border border-lavender/40 bg-lavender/10 p-5 text-sm text-foreground/80">
              <Loader2 className="h-4 w-4 animate-spin text-lavender-deep" />
              Your gentle reflection is on its way — it will appear right here.
            </div>
          ) : ai ? (
            <>
              <div className="rounded-2xl border border-lavender/40 bg-gradient-to-br from-lavender/15 to-blush/10 p-5">
                <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-lavender-deep">
                  <Sparkles className="h-3.5 w-3.5" />
                  A gentle reflection
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                  {ai.reflection}
                </p>
              </div>

              {ai.insight && (
                <div className="rounded-2xl border border-gold/30 bg-gold/5 p-5">
                  <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-gold">
                    <Lightbulb className="h-3.5 w-3.5" />
                    Insight
                  </div>
                  <p className="text-sm leading-relaxed text-foreground/90">
                    {ai.insight}
                  </p>
                </div>
              )}

              {ai.practice && (
                <div className="rounded-2xl border border-blush/40 bg-blush/15 p-5">
                  <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-gold-deep">
                    <Heart className="h-3.5 w-3.5" />
                    A small next step
                  </div>
                  <p className="font-serif text-lg text-foreground">
                    {ai.practice.title}
                  </p>
                  <p className="mt-1 text-sm text-foreground/85">
                    {ai.practice.description}
                  </p>
                  <Link
                    to={ai.practice.href}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background transition-transform hover:scale-[1.02]"
                  >
                    Open practice
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}

              {ai.followUp && (
                <div className="rounded-2xl border border-border/50 bg-card p-5">
                  <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <Compass className="h-3.5 w-3.5" />
                    A question to carry with you
                  </div>
                  <p className="font-serif text-base italic text-foreground">
                    "{ai.followUp}"
                  </p>
                </div>
              )}
            </>
          ) : entry.supportive_reply ? (
            <div className="rounded-2xl border border-lavender/40 bg-lavender/10 p-5">
              <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-lavender-deep">
                <Sparkles className="h-3.5 w-3.5" />
                A gentle reflection
              </div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                {entry.supportive_reply}
              </p>
            </div>
          ) : (
            <p className="text-center text-xs italic text-muted-foreground">
              No reflection yet — tap "Receive reflection" at the bottom whenever you're ready.
            </p>
          )}
          </div>

          <JournalEntrySummary
            id={entry.id}
            mood={entry.mood}
            prompt={entry.prompt}
            isAudio={!!(entry as { audio_path?: string | null }).audio_path}
          />

          {(ai || entry.supportive_reply) && (
            <JournalFeelingLinks entryId={entry.id} content={entry.content} />
          )}

          <ReflectionSupport
            onClose={onClose}
            additionalSupport={
              <>
                <RecommendedTools
                  prompt={entry.prompt}
                  intro="Tools that may support this reflection"
                />
                {readingForPromptText(entry.prompt).length > 0 && (
                  <div className="rounded-2xl border border-border/50 bg-card/70 p-4">
                    <p className="mb-2 text-xs text-muted-foreground">
                      If you'd like to read further on this theme:
                    </p>
                    <PromptReading links={readingForPromptText(entry.prompt)} label="Learn Hub" />
                  </div>
                )}
              </>
            }
          />
        </div>

        <div
          className="relative z-[120] flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border/40 bg-card/95 px-6 py-4 backdrop-blur sm:px-8"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px) + 5.25rem, 1rem)" }}
        >
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            className="inline-flex touch-manipulation items-center gap-1.5 rounded-full bg-gradient-to-r from-lavender-deep to-gold-deep px-4 py-2.5 text-xs font-medium text-white shadow-sm transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
          >
            {regenerating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {status === "retrying"
                  ? `Retrying (${attempt}/${maxAttempts})…`
                  : "✨ Preparing your reflection…"}
              </>
            ) : status === "error" ? (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                Try again
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                {ai || entry.supportive_reply ? "Receive new reflection" : "Receive reflection"}
              </>
            )}
          </button>
          {status === "generating" && (
            <span
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-1.5 rounded-full bg-lavender/15 px-3 py-1.5 text-[11px] font-medium text-lavender-deep"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Reaching the reflection service…
            </span>
          )}
          {status === "retrying" && (
            <span
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1.5 text-[11px] font-medium text-amber-700"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Connection was slow — retrying (attempt {attempt} of {maxAttempts})…
            </span>
          )}
          {status === "error" && (
            <span
              role="alert"
              aria-live="assertive"
              className="inline-flex max-w-full items-start gap-1.5 rounded-2xl bg-rose-500/10 px-3 py-1.5 text-[11px] font-medium text-rose-700"
            >
              <AlertCircle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
              <span className="leading-snug">
                {errorMessage ?? "Couldn't reach the reflection service."} A gentle
                fallback has been shown — tap "Try again" when you're ready.
              </span>
            </span>
          )}
          {status === "saved" && (
            <span
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1.5 text-[11px] font-medium text-emerald-600 transition-opacity"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Saved to your journal
            </span>
          )}
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex touch-manipulation items-center gap-1.5 rounded-full px-3 py-2 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Release this entry
          </button>
        </div>
      </div>
    </div>
  );
}


function EmptyState({ onStart, isAuthed, authReady }: { onStart: () => void; isAuthed: boolean; authReady: boolean }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-border/40 bg-card/70 p-8 text-center shadow-sm">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#e9defb] to-[#f1d6ec] shadow-inner dark:from-accent dark:to-muted">
        <BookOpen className="h-5 w-5 text-[#7657a3] dark:text-foreground" />
      </div>
      <p className="font-serif text-lg text-foreground">
        No reflections yet
      </p>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {authReady && !isAuthed
          ? "Write your first reflection below — it will save to this browser. Sign in any time to keep your reflections safely in your private journal."
          : "Your saved reflections will appear here once you've written your first one."}
      </p>
      <button
        onClick={onStart}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#9b7cc7] to-[#c48bb8] px-6 py-2.5 text-sm font-medium text-white shadow-md transition-transform hover:scale-[1.02]"
      >
        Start your first reflection
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function TappingSuggestion({ text }: { text: string }) {
  const suggestion = useMemo(() => suggestTappingForText(text), [text]);
  if (!suggestion || text.trim().length < 12) return null;
  return (
    <Link
      to="/tapping"
      className="mt-3 flex items-center gap-3 rounded-2xl border border-gold/40 bg-gradient-to-br from-cream/60 to-blush/30 p-3 text-sm text-foreground transition-all hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-[var(--shadow-gold)]"
    >
      <span className="text-xl">{suggestion.emoji}</span>
      <span className="flex-1">
        <span className="block text-[10px] font-medium uppercase tracking-wider text-[var(--gold-deep)]">
          You might find this helpful
        </span>
        <span className="font-serif">🤲 {suggestion.title}</span>
      </span>
      <ArrowRight className="h-4 w-4 text-[var(--gold-deep)]" />
    </Link>
  );
}

function RecommendedTools({
  prompt,
  intro,
}: {
  prompt?: string | null;
  intro: string;
}) {
  const recs: CategoryRecommendation[] = useMemo(
    () => recommendationsForPrompt(prompt),
    [prompt],
  );
  if (recs.length === 0) return null;
  return (
    <div className="mt-4 rounded-2xl border border-lavender/30 bg-lavender/5 px-4 py-3">
      <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-lavender-deep">
        💛 {intro}
      </p>
      <div className="flex flex-wrap gap-2">
        {recs.map((r) => (
          <Link
            key={r.to + r.label}
            to={r.to}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-lavender/40 bg-card px-3 py-1.5 text-xs font-medium text-foreground/85 transition-all hover:-translate-y-0.5 hover:border-lavender/60 hover:bg-lavender/10"
          >
            <span aria-hidden>{r.emoji}</span>
            <span>{r.label}</span>
          </Link>
        ))}
      </div>
      <p className="mt-2 text-[10px] italic text-muted-foreground">
        Only if it feels supportive. There's no pressure to open anything.
      </p>
    </div>
  );
}




function GentleNoticeGate({ onContinue }: { onContinue: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="gentle-notice-title"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/85 px-4 py-8 backdrop-blur-md"
    >
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-gold/30 bg-card shadow-2xl">
        <div className="bg-gradient-to-br from-gold/10 via-card to-[#f3e7f7]/40 p-6 dark:to-background sm:p-8">
          <div className="mb-4 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-foreground">
            <Heart className="h-3.5 w-3.5 text-[var(--gold-deep)]" /> Before entering Recovery Journal
          </div>
          <h2
            id="gentle-notice-title"
            className="font-serif text-2xl leading-snug text-foreground sm:text-3xl"
          >
            💛 A Gentle Note Before You Begin
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
            <p>
              Some journal prompts may encourage reflection on emotions,
              memories, relationships, trauma, grief, or other personal
              experiences.
            </p>
            <p>Please only engage with prompts that feel supportive for you today.</p>
            <div>
              <p className="font-medium text-foreground">You are always free to:</p>
              <ul className="mt-1.5 space-y-1 ps-4 text-foreground/85">
                <li className="list-disc">Skip any prompt</li>
                <li className="list-disc">Take a break</li>
                <li className="list-disc">Choose a different activity</li>
                <li className="list-disc">Return later if needed</li>
              </ul>
            </div>
            <p>
              There is no pressure to share or explore anything that feels
              overwhelming. Your wellbeing comes first.
            </p>
          </div>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-card"
            >
              Choose a different activity
            </Link>
            <button
              type="button"
              onClick={onContinue}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background shadow-sm transition-transform hover:scale-[1.02]"
            >
              Continue to Journal
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Collapsible section in the Dream Journal style — gold-tinted header,
   compact summary line, gentle expand/collapse. */
function JournalSection({
  id,
  icon,
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border transition-colors ${
        open ? "border-gold/55 bg-background/70" : "border-border/65 bg-background/35"
      }`}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`journal-section-${id}`}
        onClick={onToggle}
        className="grid min-h-[60px] w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3.5 py-2.5 text-start transition-colors hover:bg-gold/10"
      >
        <span
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--gold-deep)] ${
            open ? "bg-gold/25 ring-1 ring-gold" : "bg-muted"
          }`}
          aria-hidden
        >
          {icon}
        </span>
        <span className="min-w-0">
          <span className="block font-serif text-base text-foreground">{title}</span>
          <span className="block truncate text-xs font-normal text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--gold-deep)] transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open ? (
        <div id={`journal-section-${id}`} className="border-t border-gold/20 px-4 pb-4 pt-2 sm:px-5">
          {children}
        </div>
      ) : null}
    </section>
  );
}
